import React, {
  useState,
  useContext,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import { EstimateContext } from "../Estimate";
import { useEstimateState } from "../EstimateContext";
import { calculateConstructionDesign } from "../../../../../api/projectEstimatesApi";
import "./QualityTab.css";

// Постоянные колонки на вкладке «Контроль качества»
const QUALITY_FIXED_ROLE_1 = "РО 5";
const QUALITY_FIXED_ROLE_2 = "Норм.контроль";

const defaultQualityRole = (roleName) => {
  const isNorm = roleName === "Норм.контроль" || roleName === "Норм Контроль";
  return {
    role: roleName,
    average_salary: isNorm ? 0 : null,
    day_efforts: { direct: 0, bonus: 0 },
    money_expenditures: { direct: 0, bonus: 0 },
  };
};

const QualityTab = forwardRef(({ isEditable = true }, ref) => {
  const estimateContext = useContext(EstimateContext);
  const {
    getSectionData,
    updateEstimateSection,
    estimateData,
    sectionDetails: contextSections,
    setEstimateData,
    performSave,
  } = useEstimateState();

  // Получаем projectId из контекста или из URL как fallback
  const projectId =
    estimateContext?.projectId || window.location.pathname.split("/").pop();

  // Получаем данные контроля качества из нового контекста состояния
  const qualityControlData = useMemo(
    () => getSectionData("quality_control") || {},
    [getSectionData],
  );

  // Постоянные колонки: РО 5 и Норм.контроль, остальные — из API. Порядок по order_index.
  const displayRoles = useMemo(() => {
    const fromApi = qualityControlData.roles || [];
    const sorted = [...fromApi].sort(
      (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0),
    );
    const ro5 =
      sorted.find(
        (r) => r.role === QUALITY_FIXED_ROLE_1 || r.role === "РО 5",
      ) || defaultQualityRole(QUALITY_FIXED_ROLE_1);
    const norm =
      sorted.find(
        (r) =>
          r.role === QUALITY_FIXED_ROLE_2 ||
          r.role === "Норм.контроль" ||
          r.role === "Норм Контроль",
      ) || defaultQualityRole(QUALITY_FIXED_ROLE_2);
    const others = sorted.filter(
      (r) =>
        r.role !== QUALITY_FIXED_ROLE_1 &&
        r.role !== "РО 5" &&
        r.role !== QUALITY_FIXED_ROLE_2 &&
        r.role !== "Норм.контроль" &&
        r.role !== "Норм Контроль",
    );
    return [ro5, norm, ...others];
  }, [qualityControlData.roles]);

  // Разделы с вкладки «Рабочее проектирование» — только они доступны для добавления
  const workTabSections = useMemo(() => {
    const list = [];
    const seen = new Set();
    const add = (arr) => {
      (arr || []).forEach((item) => {
        const id = item?.section?.id;
        const name = item?.section?.name;
        if (id != null && name != null && !seen.has(id)) {
          seen.add(id);
          list.push({
            id,
            name,
            description: item?.section?.description ?? "",
          });
        }
      });
    };
    add(estimateData?.construction_design);
    add(estimateData?.construction_design_project);
    return list;
  }, [
    estimateData?.construction_design,
    estimateData?.construction_design_project,
  ]);

  // Функция для извлечения ставок специалистов (по displayRoles)
  // ЗП задаётся только для "Норм Контроль", у остальных ролей — null
  const getQualityControlRoleSalariesFromApi = useCallback(() => {
    return displayRoles.map((role) => {
      const isNorm = role?.role === "Норм.контроль" || role?.role === "Норм Контроль";
      return {
        role: role.role,
        average_salary: isNorm ? role.average_salary : null,
        section_id: role.section_id ?? null,
      };
    });
  }, [displayRoles]);

  // Роли для таблицы
  const roles = displayRoles;
  const isNormativeRole = useCallback(
    (r) => r?.role === "Норм.контроль" || r?.role === "Норм Контроль",
    [],
  );
  // Используем данные с сервера для include_bonuses
  const includeBonuses = qualityControlData.include_bonuses ?? true;
  const qualityControlPercent = qualityControlData.quality_control_percent;
  const directBonusPercent = qualityControlData.direct_bonus_percent;
  const normativeEffortsInit =
    qualityControlData.normative_control_efforts ??
    roles.find(isNormativeRole)?.day_efforts?.direct;

  // Состояния для редактируемых полей
  const [editingFields, setEditingFields] = useState({});
  const [savingFields, setSavingFields] = useState({});
  const [localValues, setLocalValues] = useState({
    include_bonuses: includeBonuses,
    quality_control_percent: qualityControlPercent,
    direct_bonus_percent: directBonusPercent,
    normative_control_efforts: normativeEffortsInit,
    quality_control_role_salaries: [],
  });

  // Модалка добавления раздела: только разделы из Рабочего проектирования
  const [showAddQualitySectionModal, setShowAddQualitySectionModal] =
    useState(false);
  const [selectedQualitySections, setSelectedQualitySections] = useState([]);
  const [qualitySectionSearch, setQualitySectionSearch] = useState("");

  // Обновляем локальные значения при изменении данных из контекста
  useEffect(() => {
    // Проверяем, есть ли активные поля в редактировании
    const hasActiveEditing = Object.values(editingFields).some(
      (isEditing) => isEditing,
    );
    const hasActiveSaving = Object.values(savingFields).some(
      (isSaving) => isSaving,
    );

    if (hasActiveEditing || hasActiveSaving) {
      return;
    }

    // Проверяем, есть ли пользовательские изменения в localValues.
    // Для quality_control_role_salaries: пустой локальный массив при наличии данных в API
    // считаем не пользовательским изменением, а начальным состоянием — тогда подставляем данные из контекста.
    const apiRoleSalaries = getQualityControlRoleSalariesFromApi();
    const salariesDiffer =
      JSON.stringify(localValues.quality_control_role_salaries) !==
      JSON.stringify(apiRoleSalaries);
    const userChangedSalaries =
      localValues.quality_control_role_salaries.length > 0 && salariesDiffer;

    const hasUserChanges =
      localValues.include_bonuses !==
        (qualityControlData.include_bonuses ?? true) ||
      localValues.quality_control_percent !==
        qualityControlData.quality_control_percent ||
      localValues.direct_bonus_percent !==
        qualityControlData.direct_bonus_percent ||
      localValues.normative_control_efforts !==
        (qualityControlData.normative_control_efforts ??
          roles.find(isNormativeRole)?.day_efforts?.direct) ||
      userChangedSalaries;

    if (hasUserChanges) {
      return;
    }

    const newValues = {
      include_bonuses: qualityControlData.include_bonuses ?? true,
      quality_control_percent: qualityControlData.quality_control_percent,
      direct_bonus_percent: qualityControlData.direct_bonus_percent,
      normative_control_efforts:
        qualityControlData.normative_control_efforts ??
        roles.find(isNormativeRole)?.day_efforts?.direct,
      quality_control_role_salaries: getQualityControlRoleSalariesFromApi(),
    };

    const isEqual =
      localValues.include_bonuses === newValues.include_bonuses &&
      localValues.quality_control_percent ===
        newValues.quality_control_percent &&
      localValues.direct_bonus_percent === newValues.direct_bonus_percent &&
      localValues.normative_control_efforts ===
        newValues.normative_control_efforts &&
      JSON.stringify(localValues.quality_control_role_salaries) ===
        JSON.stringify(newValues.quality_control_role_salaries);

    if (!isEqual) {
      setLocalValues(newValues);
    }
  }, [
    qualityControlData,
    editingFields,
    savingFields,
    getQualityControlRoleSalariesFromApi,
    localValues,
    roles,
    isNormativeRole,
  ]);

  // Синхронизируем локальные изменения с контекстом в реальном времени
  useEffect(() => {
    // Не перезаписываем контекст пустым quality_control_role_salaries, если в контексте уже есть роли
    // (например, после переключения вкладки локальное состояние ещё пустое)
    const contextRoles =
      estimateData?.quality_control?.quality_control_role_salaries ||
      estimateData?.quality_control?.roles ||
      [];
    const salariesToSync =
      localValues.quality_control_role_salaries.length > 0
        ? localValues.quality_control_role_salaries
        : contextRoles.length > 0
          ? contextRoles.map((r) => {
              const isNorm = r?.role === "Норм.контроль" || r?.role === "Норм Контроль";
              return {
                role: r.role,
                average_salary: isNorm ? (r.average_salary ?? null) : null,
                section_id: r.section_id ?? null,
              };
            })
          : [];

    const currentEstimateData = estimateData || {};
    const updatedEstimateData = {
      ...currentEstimateData,
      quality_control: {
        ...currentEstimateData.quality_control,
        include_bonuses: localValues.include_bonuses,
        quality_control_percent: localValues.quality_control_percent,
        direct_bonus_percent: localValues.direct_bonus_percent,
        normative_control_efforts: localValues.normative_control_efforts,
        quality_control_role_salaries: salariesToSync,
      },
    };

    // Проверяем, действительно ли нужно обновлять контекст
    const currentQualityControl = currentEstimateData.quality_control || {};
    const contextSalariesNorm =
      (currentQualityControl.quality_control_role_salaries ||
        currentQualityControl.roles ||
        []
      ).map((r) => {
        const isNorm = r?.role === "Норм.контроль" || r?.role === "Норм Контроль";
        return {
          role: r.role,
          average_salary: isNorm ? (r.average_salary ?? null) : null,
          section_id: r.section_id ?? null,
        };
      });
    const needsUpdate =
      currentQualityControl.include_bonuses !== localValues.include_bonuses ||
      currentQualityControl.quality_control_percent !==
        localValues.quality_control_percent ||
      currentQualityControl.direct_bonus_percent !==
        localValues.direct_bonus_percent ||
      currentQualityControl.normative_control_efforts !==
        localValues.normative_control_efforts ||
      currentQualityControl.vk_control_efforts !==
        localValues.vk_control_efforts ||
      JSON.stringify(contextSalariesNorm) !==
        JSON.stringify(salariesToSync);

    if (needsUpdate) {
      setEstimateData(updatedEstimateData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValues, setEstimateData]); // estimateData намеренно исключен из зависимостей для предотвращения бесконечного цикла

  // Определяем колонки на основе ролей из API
  const qualityColumns = roles.map((role) => role.role) || [];

  // Получаем данные для таблицы из API
  const getTableData = () => {
    const laborData = roles.map((role) =>
      (isNormativeRole(role)
        ? parseFloat(localValues.normative_control_efforts)
        : role.day_efforts?.direct
      ).toFixed(1),
    );

    const bonusDaysData = roles.map((role) =>
      (role.day_efforts?.bonus).toFixed(1),
    );

    const directCostsData = roles.map(
      (role) => `₸ ${(role.money_expenditures?.direct).toLocaleString()}`,
    );

    const bonusMoneyData = roles.map(
      (role) => `₸ ${(role.money_expenditures?.bonus).toLocaleString()}`,
    );

    return {
      labor: laborData,
      bonusDays: bonusDaysData,
      directCosts: directCostsData,
      bonusMoney: bonusMoneyData,
    };
  };

  const tableData = getTableData();

  // Функция для калькуляции (overrideEstimateData — при добавлении/удалении разделов)
  const calculateQualityData = async (
    updatedQualityData,
    overrideEstimateData,
  ) => {
    try {
      const currentEstimateData = overrideEstimateData || estimateData || {};
      const currentSectionsData = contextSections || {};

      const updatedEstimateData = {
        ...currentEstimateData,
        quality_control: {
          ...currentEstimateData.quality_control,
          include_bonuses: updatedQualityData.include_bonuses,
          quality_control_percent: updatedQualityData.quality_control_percent,
          direct_bonus_percent: updatedQualityData.direct_bonus_percent,
          normative_control_efforts:
            updatedQualityData.normative_control_efforts,
          quality_control_role_salaries:
            updatedQualityData.quality_control_role_salaries,
        },
      };

      setEstimateData(updatedEstimateData);

      const calculationResult = await calculateConstructionDesign(
        projectId,
        updatedEstimateData,
        currentSectionsData,
      );

      if (estimateContext && estimateContext.updateHeaderData) {
        estimateContext.updateHeaderData({
          contract_value: calculationResult.contract_value,
          contract_value_before_nds:
            calculationResult.contract_value_before_nds,
          contract_value_construction:
            calculationResult.contract_value_construction,
          contract_value_construction_percent:
            calculationResult.contract_value_construction_percent,
          cost_price: calculationResult.cost_price,
          cost_price_percent: calculationResult.cost_price_percent,
          difference: calculationResult.difference,
          nds_value: calculationResult.nds_value,
          indirect_expenditures: calculationResult.indirect_expenditures,
          indirect_expenditures_percent:
            calculationResult.indirect_expenditures_percent,
          pure_income: calculationResult.pure_income,
          pure_income_percent: calculationResult.pure_income_percent,
        });
      }

      // Сохраняем наш список ролей, подставляем рассчитанные поля из ответа
      if (calculationResult && calculationResult.quality_control) {
        const rolesWeSent =
          updatedEstimateData.quality_control?.quality_control_role_salaries ??
          [];
        const rolesFromApi = calculationResult.quality_control.roles ?? [];
        const mergedRoles = rolesWeSent.map((ourRole) => {
          const fromApi = rolesFromApi.find((r) => r.role === ourRole.role);
          const merged = fromApi ? { ...fromApi, ...ourRole } : ourRole;
          // Приоритет section_id из ответа API (бэкенд теперь везде отдаёт section_id)
          merged.section_id = fromApi?.section_id ?? ourRole.section_id ?? null;
          return merged;
        });
        const mergedQualityControl = {
          ...calculationResult.quality_control,
          roles: mergedRoles,
        };
        updateEstimateSection("quality_control", mergedQualityControl);
      }

      setLocalValues((prev) => {
        const rolesWeSent =
          updatedEstimateData.quality_control?.quality_control_role_salaries ??
          [];
        const serverRoles = calculationResult.quality_control?.roles ?? [];
        const currentRoles = prev.quality_control_role_salaries || [];
        const mergedRoles = rolesWeSent.map((ourRole, index) => {
          const fromApi = serverRoles.find((r) => r.role === ourRole.role);
          const currentRole = currentRoles[index];
          const isNorm = ourRole?.role === "Норм.контроль" || ourRole?.role === "Норм Контроль";
          let salary;
          if (isNorm) {
            salary =
              currentRole && currentRole.average_salary !== 0 && currentRole.average_salary !== null
                ? currentRole.average_salary
                : fromApi?.average_salary ?? ourRole.average_salary ?? null;
          } else {
            salary = null;
          }
          return {
            role: ourRole.role,
            average_salary: salary,
            section_id: fromApi?.section_id ?? ourRole.section_id ?? currentRole?.section_id ?? null,
          };
        });
        return {
          ...prev,
          quality_control_percent:
            calculationResult.quality_control?.quality_control_percent ??
            prev.quality_control_percent,
          direct_bonus_percent:
            calculationResult.quality_control?.direct_bonus_percent ??
            prev.direct_bonus_percent,
          quality_control_role_salaries: mergedRoles,
        };
      });

      return calculationResult;
    } catch (error) {
      throw error;
    }
  };

  // Обработчик для изменения чекбокса "Учитывать бонусы при расчете"
  const handleCheckboxChange = async (fieldName, checked) => {
    try {
      setLocalValues((prev) => ({
        ...prev,
        [fieldName]: checked,
      }));

      // Вызываем калькуляцию с обновленными данными
      await calculateQualityData({
        ...localValues,
        [fieldName]: checked,
      });
    } catch (error) {
      throw error;
    }
  };

  // Обработчики для редактируемых полей
  const handleEditField = (fieldName, value) => {
    setEditingFields((prev) => ({ ...prev, [fieldName]: true }));
    setLocalValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  // Обработчик для изменения ставки специалиста
  // const handleRoleSalaryChange = (roleIndex, newSalary) => { /* unused */ };

  // Обработчик для сохранения изменений в ставке специалиста
  const handleSaveRoleSalary = async (roleIndex, newSalary) => {
    try {
      setSavingFields((prev) => ({
        ...prev,
        [`role_salary_${roleIndex}`]: true,
      }));

      const baseSalaries = localValues.quality_control_role_salaries || [];
      const updatedRoleSalaries = roles.map((r, index) => {
        const current = baseSalaries[index];
        const isNorm = r?.role === "Норм.контроль" || r?.role === "Норм Контроль";
        let salary;
        if (index === roleIndex) {
          salary = parseFloat(newSalary);
        } else if (isNorm) {
          salary = current?.average_salary ?? r.average_salary ?? null;
        } else {
          salary = null;
        }
        return {
          role: r.role,
          average_salary: salary,
          section_id: r.section_id ?? current?.section_id ?? null,
        };
      });

      setLocalValues((prev) => ({
        ...prev,
        quality_control_role_salaries: updatedRoleSalaries,
      }));

      // Вызываем калькуляцию с обновленными данными
      await calculateQualityData({
        ...localValues,
        quality_control_role_salaries: updatedRoleSalaries,
      });

      setEditingFields((prev) => ({
        ...prev,
        [`role_salary_${roleIndex}`]: false,
      }));
    } catch (error) {
      throw error;
    } finally {
      setSavingFields((prev) => ({
        ...prev,
        [`role_salary_${roleIndex}`]: false,
      }));
    }
  };

  // Обработчик для отмены редактирования ставки специалиста
  const handleCancelRoleSalary = (roleIndex) => {
    setEditingFields((prev) => ({
      ...prev,
      [`role_salary_${roleIndex}`]: false,
    }));
    const role = roles[roleIndex];
    const isNorm = role?.role === "Норм.контроль" || role?.role === "Норм Контроль";
    const originalSalary = isNorm
      ? (role?.average_salary ?? qualityControlData.roles?.[roleIndex]?.average_salary ?? null)
      : null;
    const baseSalaries = localValues.quality_control_role_salaries || [];
    setLocalValues((prev) => ({
      ...prev,
      quality_control_role_salaries: roles.map((r, index) => {
        const current = baseSalaries[index];
        const isNormRole = r?.role === "Норм.контроль" || r?.role === "Норм Контроль";
        let salary;
        if (index === roleIndex) {
          salary = originalSalary;
        } else if (isNormRole) {
          salary = current?.average_salary ?? r.average_salary ?? null;
        } else {
          salary = null;
        }
        return {
          role: r.role,
          average_salary: salary,
          section_id: r.section_id ?? current?.section_id ?? null,
        };
      }),
    }));
  };

  const handleSaveField = async (fieldName, value) => {
    try {
      setSavingFields((prev) => ({ ...prev, [fieldName]: true }));

      const parsedValue = parseFloat(value);

      // Обновляем локальные значения
      setLocalValues((prev) => ({
        ...prev,
        [fieldName]: parsedValue,
      }));

      // Вызываем калькуляцию с обновленными данными
      await calculateQualityData({
        ...localValues,
        [fieldName]: parsedValue,
      });

      setEditingFields((prev) => ({ ...prev, [fieldName]: false }));
    } catch (error) {
      throw error;
    } finally {
      setSavingFields((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleCancelEdit = (fieldName) => {
    setEditingFields((prev) => ({ ...prev, [fieldName]: false }));
    setLocalValues((prev) => ({
      ...prev,
      [fieldName]:
        fieldName === "quality_control_percent"
          ? qualityControlPercent
          : fieldName === "direct_bonus_percent"
            ? directBonusPercent
            : fieldName === "normative_control_efforts"
              ? (qualityControlData.normative_control_efforts ??
                roles.find(isNormativeRole)?.day_efforts?.direct)
              : prev[fieldName],
    }));
  };

  const handleAddQualitySection = () => {
    setSelectedQualitySections([]);
    setQualitySectionSearch("");
    setShowAddQualitySectionModal(true);
  };

  const handleCancelAddQualitySection = () => {
    setShowAddQualitySectionModal(false);
    setSelectedQualitySections([]);
    setQualitySectionSearch("");
  };

  const handleConfirmAddQualitySection = async () => {
    if (selectedQualitySections.length === 0) return;
    const toAdd = workTabSections.filter((s) =>
      selectedQualitySections.includes(s.id),
    );
    if (toAdd.length === 0) return;

    const newRoleObjects = toAdd.map((s) => ({
      role: s.name,
      average_salary: null,
      section_id: s.id,
      day_efforts: { direct: 0, bonus: 0 },
      money_expenditures: { direct: 0, bonus: 0 },
    }));
    const newRoles = [...displayRoles, ...newRoleObjects];
    const newSalaries = newRoles.map((r) => {
      const isNorm = r?.role === "Норм.контроль" || r?.role === "Норм Контроль";
      return {
        role: r.role,
        average_salary: isNorm ? (r.average_salary ?? null) : null,
        section_id: r.section_id ?? null,
      };
    });

    setLocalValues((prev) => ({
      ...prev,
      quality_control_role_salaries: newSalaries,
    }));
    const newEstimateData = {
      ...estimateData,
      quality_control: {
        ...estimateData?.quality_control,
        roles: newRoles,
      },
    };
    setEstimateData(newEstimateData);

    try {
      await calculateQualityData(
        {
          ...localValues,
          quality_control_role_salaries: newSalaries,
        },
        newEstimateData,
      );
    } catch (e) {
      console.error(e);
    }

    setShowAddQualitySectionModal(false);
    setSelectedQualitySections([]);
    setQualitySectionSearch("");
  };

  const handleRemoveQualityColumn = async (roleIndex) => {
    if (roleIndex < 2) return; // РО 5 и Норм.контроль — постоянные
    const newRoles = displayRoles.filter((_, i) => i !== roleIndex);
    const newSalaries = newRoles.map((r) => {
      const isNorm = r?.role === "Норм.контроль" || r?.role === "Норм Контроль";
      return {
        role: r.role,
        average_salary: isNorm ? (r.average_salary ?? null) : null,
        section_id: r.section_id ?? null,
      };
    });
    setEditingFields((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith("role_salary_")) delete next[k];
      });
      return next;
    });
    setSavingFields((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith("role_salary_")) delete next[k];
      });
      return next;
    });
    setLocalValues((prev) => ({
      ...prev,
      quality_control_role_salaries: newSalaries,
    }));
    const newEstimateData = {
      ...estimateData,
      quality_control: {
        ...estimateData?.quality_control,
        roles: newRoles,
      },
    };
    setEstimateData(newEstimateData);
    try {
      await calculateQualityData(
        {
          ...localValues,
          quality_control_role_salaries: newSalaries,
        },
        newEstimateData,
      );
    } catch (e) {
      console.error(e);
    }
  };

  const [qualityDragOverCol, setQualityDragOverCol] = useState(null);

  const reorderArray = (arr, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return arr;
    const list = [...arr];
    const [removed] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, removed);
    return list;
  };

  const handleReorderQualityColumns = async (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const newRoles = reorderArray(displayRoles, fromIndex, toIndex);
    const newSalaries = newRoles.map((r) => {
      const isNorm = r?.role === "Норм.контроль" || r?.role === "Норм Контроль";
      return {
        role: r.role,
        average_salary: isNorm ? (r.average_salary ?? null) : null,
        section_id: r.section_id ?? null,
      };
    });
    setLocalValues((prev) => ({
      ...prev,
      quality_control_role_salaries: newSalaries,
    }));
    const newEstimateData = {
      ...estimateData,
      quality_control: {
        ...estimateData?.quality_control,
        roles: newRoles,
      },
    };
    setEstimateData(newEstimateData);
    try {
      await calculateQualityData(
        { ...localValues, quality_control_role_salaries: newSalaries },
        newEstimateData,
      );
    } catch (e) {
      console.error(e);
    }
  };

  // Компонент для редактируемого поля
  const EditableField = ({
    fieldName,
    value,
    isCurrency = false,
    isPercent = false,
    onSave,
    onCancel,
    isEditing,
    onEdit,
    isSaving,
  }) => {
    const [localValue, setLocalValue] = useState(value);
    const inputRef = React.useRef(null);
    const originalValueRef = React.useRef(value);

    const computeWidthPx = (val) => {
      const text =
        val === undefined || val === null || val === "" ? "0" : String(val);
      const charCount = text.length;
      return Math.max(30, charCount * 10 + 16);
    };

    // Обновляем локальное значение при изменении props
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    // Сохраняем исходное значение при входе в режим редактирования
    useEffect(() => {
      if (isEditing) {
        originalValueRef.current = value;
      }
    }, [isEditing, value]);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);

    const currentValue = isEditing ? localValue : value;

    if (isEditing) {
      const w = computeWidthPx(currentValue);
      return (
        <div
          style={{ position: "relative", width: `${w}px`, margin: "0 auto" }}
        >
          <input
            type="number"
            step={isPercent ? "0.01" : "1"}
            value={currentValue}
            ref={inputRef}
            onChange={(e) => setLocalValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSave(fieldName, localValue);
              } else if (e.key === "Escape") {
                setLocalValue(value);
                onCancel(fieldName);
              }
            }}
            style={{
              width: `${w}px`,
              padding: "6px 10px",
              border: "1.5px dashed #B8C2D1",
              borderRadius: "6px",
              background: "#FFFFFF",
              fontSize: "14px",
              textAlign: "center",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#007bff")}
            onBlur={(e) => {
              e.target.style.borderColor = "#ccc";
              
              // Проверяем, изменилось ли значение от исходного
              const originalStr = String(originalValueRef.current ?? "");
              const currentStr = String(localValue ?? "");
              
              // Если значение не изменилось — просто отменяем редактирование
              if (originalStr === currentStr) {
                onCancel(fieldName);
                return;
              }
              
              // Если значение пустое или некорректное — восстанавливаем исходное
              const numValue = parseFloat(localValue);
              if (localValue === "" || Number.isNaN(numValue)) {
                setLocalValue(originalValueRef.current);
                onCancel(fieldName);
                return;
              }
              
              // Автоматически сохраняем при потере фокуса только если значение валидно и изменилось
              onSave(fieldName, localValue);
            }}
            disabled={!isEditable || isSaving}
          />
        </div>
      );
    }

    const displayValue = isCurrency
      ? `₸ ${parseFloat(value).toLocaleString()}`
      : isPercent
        ? `${value}`
        : value;
    const wView = computeWidthPx(displayValue);

    return (
      <div
        style={{ width: `${wView}px`, margin: "0 auto", textAlign: "center" }}
      >
        <span
          style={{
            fontSize: "14px",
            display: "inline-block",
            width: `${wView}px`,
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: "6px",
            background: !isEditable ? "#F4F6FA" : "#F9FBFF",
            color: "#1F2937",
            cursor: !isEditable ? "default" : "pointer",
          }}
          onClick={() => (!isEditable ? undefined : onEdit(fieldName, value))}
        >
          {isPercent ? `${displayValue}` : displayValue}
        </span>
      </div>
    );
  };

  // Компонент для редактируемой ставки специалиста
  const EditableRoleSalary = ({
    roleIndex,
    role,
    averageSalary,
    onSave,
    onCancel,
    isEditing,
    onEdit,
    isSaving,
  }) => {
    const [localValue, setLocalValue] = useState(averageSalary);
    const inputRef = React.useRef(null);
    const originalValueRef = React.useRef(averageSalary);

    const computeWidthPx = (val) => {
      const text =
        val === undefined || val === null || val === "" ? "0" : String(val);
      const charCount = text.length;
      return Math.max(30, charCount * 10 + 16);
    };

    // Обновляем локальное значение при изменении props
    useEffect(() => {
      setLocalValue(averageSalary);
    }, [averageSalary]);

    // Сохраняем исходное значение при входе в режим редактирования
    useEffect(() => {
      if (isEditing) {
        originalValueRef.current = averageSalary;
      }
    }, [isEditing, averageSalary]);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);

    const currentValue = isEditing ? localValue : averageSalary;

    if (isEditing) {
      const w = computeWidthPx(currentValue);
      return (
        <div
          style={{ position: "relative", width: `${w}px`, margin: "0 auto" }}
        >
          <input
            type="number"
            step="1000"
            value={currentValue}
            ref={inputRef}
            onChange={(e) => setLocalValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSave(roleIndex, localValue);
              } else if (e.key === "Escape") {
                setLocalValue(averageSalary);
                onCancel(roleIndex);
              }
            }}
            style={{
              width: `${w}px`,
              padding: "6px 10px",
              border: "1.5px dashed #B8C2D1",
              borderRadius: "6px",
              background: "#FFFFFF",
              fontSize: "14px",
              textAlign: "center",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#007bff")}
            onBlur={(e) => {
              e.target.style.borderColor = "#ccc";
              
              // Проверяем, изменилось ли значение от исходного
              const originalStr = String(originalValueRef.current ?? "");
              const currentStr = String(localValue ?? "");
              
              // Если значение не изменилось — просто отменяем редактирование
              if (originalStr === currentStr) {
                onCancel(roleIndex);
                return;
              }
              
              // Если значение пустое или некорректное — восстанавливаем исходное
              const numValue = parseFloat(localValue);
              if (localValue === "" || Number.isNaN(numValue)) {
                setLocalValue(originalValueRef.current);
                onCancel(roleIndex);
                return;
              }
              
              // Автоматически сохраняем при потере фокуса только если значение валидно и изменилось
              onSave(roleIndex, localValue);
            }}
            disabled={!isEditable || isSaving}
          />
        </div>
      );
    }

    const displayValue = `₸ ${parseFloat(averageSalary).toLocaleString()}`;
    const wView = computeWidthPx(displayValue);

    return (
      <div
        style={{ width: `${wView}px`, margin: "0 auto", textAlign: "center" }}
      >
        <span
          style={{
            fontSize: "14px",
            display: "inline-block",
            width: `${wView}px`,
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: "6px",
            background: !isEditable ? "#F4F6FA" : "#F9FBFF",
            color: "#1F2937",
            cursor: !isEditable ? "default" : "pointer",
          }}
          onClick={() =>
            !isEditable ? undefined : onEdit(roleIndex, averageSalary)
          }
        >
          {displayValue}
        </span>
      </div>
    );
  };

  const qualityTable = [
    {
      label: "Трудозатраты (дн)",
      editable: false,
      values: tableData.labor,
      // Указываем, какая ячейка редактируемая
      editableCell: 1, // Индекс колонки "Норм Контроль"
    },
    {
      label: "Бонусы в днях (дн)",
      values: tableData.bonusDays,
    },
    {
      label: "Прямые затраты (тг)",
      values: tableData.directCosts,
    },
    {
      label: "Бонусы в деньгах (тг)",
      values: tableData.bonusMoney,
    },
  ];

  // Рассчитываем общую сумму из данных API
  const totalDirect = qualityControlData.total_money_expenditures?.direct;
  const totalBonus = qualityControlData.total_money_expenditures?.bonus;
  const total = qualityControlData.total;

  // Экспортируем функции через ref
  useImperativeHandle(ref, () => ({
    getTabData: () => {
      return {
        quality_control: {
          include_bonuses: localValues.include_bonuses,
          quality_control_percent: localValues.quality_control_percent,
          direct_bonus_percent: localValues.direct_bonus_percent,
          normative_control_efforts: localValues.normative_control_efforts,
          quality_control_role_salaries:
            localValues.quality_control_role_salaries,
        },
      };
    },
    triggerCalculation: async () => {
      try {
        // Вызываем калькуляцию с текущими локальными значениями
        const calculationResult = await calculateQualityData(localValues);
        return calculationResult;
      } catch (error) {
        throw error;
      }
    },
    handleSaveForEstimate: async () => {
      try {
        // Обновляем контекст с текущими локальными значениями перед сохранением
        const currentEstimateData = estimateData || {};
        const updatedEstimateData = {
          ...currentEstimateData,
          quality_control: {
            ...currentEstimateData.quality_control,
            include_bonuses: localValues.include_bonuses,
            quality_control_percent: localValues.quality_control_percent,
            direct_bonus_percent: localValues.direct_bonus_percent,
            normative_control_efforts: localValues.normative_control_efforts,
            quality_control_role_salaries:
              localValues.quality_control_role_salaries,
          },
        };

        // Обновляем контекст с актуальными данными
        setEstimateData(updatedEstimateData);

        // Используем функцию сохранения из контекста
        const saveResult = await performSave(projectId);

        return saveResult;
      } catch (error) {
        throw error;
      }
    },
  }));

  return (
    <div className="quality-table-block">
      {/* Чекбокс "Учитывать бонусы при расчете" и кнопка "Добавить раздел" */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          padding: "16px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #e9ecef",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <label style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
            Учитывать бонусы при расчете:
          </label>
          <label className="switch">
            <input
              type="checkbox"
              checked={localValues.include_bonuses}
              onChange={(e) =>
                handleCheckboxChange("include_bonuses", e.target.checked)
              }
              disabled={!isEditable}
            />
            <span className="slider"></span>
          </label>
        </div>
        <button
          onClick={handleAddQualitySection}
          disabled={!isEditable}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            backgroundColor: "#fff",
            color: "#2A5E38",
            border: "none",
            borderRadius: "4px",
            cursor: !isEditable ? "not-allowed" : "pointer",
            opacity: !isEditable ? 0.6 : 1,
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <span style={{ fontSize: "16px", fontWeight: "bold" }}>+</span>
          Добавить раздел
        </button>
      </div>

      {/* Редактируемые поля в одной строке */}
      <div className="quality-title-row">
        <div className="quality-title-block">
          <div className="quality-title">% доля контроля качества</div>
          <EditableField
            fieldName="quality_control_percent"
            value={localValues.quality_control_percent}
            isPercent={true}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            isEditing={editingFields.quality_control_percent}
            onEdit={handleEditField}
            isSaving={savingFields.quality_control_percent}
          />
        </div>
        <div className="quality-title-block">
          <div className="quality-title">% доля бонусов от ПЗ</div>
          <EditableField
            fieldName="direct_bonus_percent"
            value={localValues.direct_bonus_percent}
            isPercent={true}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            isEditing={editingFields.direct_bonus_percent}
            onEdit={handleEditField}
            isSaving={savingFields.direct_bonus_percent}
          />
        </div>
      </div>

      {/* Таблица с данными */}
      <div className="quality-table-mock">
        {qualityColumns.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th></th>
                {qualityColumns.map((col, i) => {
                  const isFixedRoleCol = i < 2;
                  const canDrag = isEditable && !isFixedRoleCol;
                  return (
                  <th
                    key={i}
                    draggable={canDrag}
                    onDragStart={canDrag ? (e) => {
                      if (e.dataTransfer) {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", String(i));
                      }
                    } : undefined}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
                      if (canDrag) setQualityDragOverCol(i);
                    }}
                    onDragLeave={() => setQualityDragOverCol(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setQualityDragOverCol(null);
                      if (i < 2) return;
                      const fromStr = e.dataTransfer?.getData("text/plain");
                      if (fromStr == null) return;
                      const fromIndex = parseInt(fromStr, 10);
                      if (Number.isNaN(fromIndex) || fromIndex === i) return;
                      handleReorderQualityColumns(fromIndex, i);
                    }}
                    onDragEnd={() => setQualityDragOverCol(null)}
                    style={{
                      cursor: canDrag ? "grab" : "default",
                      backgroundColor: qualityDragOverCol === i ? "rgba(26, 35, 126, 0.12)" : undefined,
                      transition: "background-color 0.15s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {canDrag && (
                        <span
                          title="Перетащите для изменения порядка"
                          style={{
                            cursor: "grab",
                            color: "#888",
                            fontSize: "14px",
                            userSelect: "none",
                          }}
                          onMouseDown={(ev) => ev.stopPropagation()}
                        >
                          ⋮⋮
                        </span>
                      )}
                      <span>{col}</span>
                      {i >= 2 && isEditable && (
                        <button
                          type="button"
                          onClick={() => handleRemoveQualityColumn(i)}
                          title="Удалить раздел"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "24px",
                            height: "24px",
                            padding: 0,
                            background: "none",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            color: "#dc3545",
                            fontSize: "18px",
                            lineHeight: 1,
                            flexShrink: 0,
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {/* Строка "Средняя ставка" — колонки по roles, чтобы совпадали с заголовком и не было лишних после удаления */}
              <tr>
                <td style={{ fontWeight: "600", color: "#1a237e" }}>
                  Средняя ставка
                </td>
                {roles.map((role, index) => {
                  const salary =
                    localValues.quality_control_role_salaries?.[index]
                      ?.average_salary ?? role.average_salary ?? 0;
                  return (
                    <td key={index} style={{ textAlign: "center" }}>
                      {isNormativeRole(role) && (
                        <EditableRoleSalary
                          roleIndex={index}
                          role={role.role}
                          averageSalary={salary}
                          onSave={handleSaveRoleSalary}
                          onCancel={handleCancelRoleSalary}
                          isEditing={editingFields[`role_salary_${index}`]}
                          onEdit={(roleIndex, value) =>
                            setEditingFields((prev) => ({
                              ...prev,
                              [`role_salary_${roleIndex}`]: true,
                            }))
                          }
                          isSaving={savingFields[`role_salary_${index}`]}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
              {qualityTable.map((row, ridx) => (
                <tr key={ridx}>
                  <td style={{ fontWeight: "500" }}>{row.label}</td>
                  {row.values.map((val, i) => (
                    <td key={i} style={{ textAlign: "center" }}>
                      {row.label === "Трудозатраты (дн)" &&
                      isNormativeRole(roles[i]) ? (
                        <EditableField
                          fieldName="normative_control_efforts"
                          value={localValues.normative_control_efforts}
                          isPercent={false}
                          onSave={handleSaveField}
                          onCancel={handleCancelEdit}
                          isEditing={editingFields.normative_control_efforts}
                          onEdit={() =>
                            handleEditField(
                              "normative_control_efforts",
                              localValues.normative_control_efforts,
                            )
                          }
                          isSaving={savingFields.normative_control_efforts}
                        />
                      ) : (
                        val
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
              fontSize: "16px",
            }}
          >
            Загрузка данных...
          </div>
        )}
      </div>

      {/* Футер с итогами */}
      <div className="quality-table-footer">
        <div className="quality-footer-main">
          <div className="quality-footer-label">ВСЕ ЗАТРАТЫ</div>
          <div className="quality-footer-sum main">
            ₸ {total.toLocaleString()}
          </div>
        </div>
        <div className="quality-footer-right">
          <div className="quality-footer-block">
            <div className="quality-footer-label">Прямые затраты</div>
            <div className="quality-footer-sum">
              ₸ {totalDirect.toLocaleString()}
            </div>
          </div>
          <div className="quality-footer-block">
            <div className="quality-footer-label">Бонусы</div>
            <div className="quality-footer-sum">
              ₸ {totalBonus.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Модалка: только разделы из Рабочего проектирования */}
      {showAddQualitySectionModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={handleCancelAddQualitySection}
        >
          <div
            className="add-section-modal"
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              minWidth: "400px",
              maxWidth: "500px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                margin: "0 0 20px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#333",
              }}
            >
              Добавление раздела в Контроль качества
            </h3>
            <p
              style={{
                margin: "0 0 12px 0",
                fontSize: "13px",
                color: "#666",
              }}
            >
              Доступны только разделы, добавленные на вкладке «Рабочее
              проектирование».
            </p>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                Выберите раздел из списка*
              </label>
              <input
                type="text"
                placeholder="Поиск"
                value={qualitySectionSearch}
                onChange={(e) => setQualitySectionSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  marginBottom: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
              <div
                style={{
                  maxHeight: "240px",
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  padding: "8px",
                  backgroundColor: "#fafafa",
                }}
              >
                {workTabSections.length === 0 ? (
                  <div
                    style={{
                      padding: "16px",
                      color: "#666",
                      fontSize: "14px",
                    }}
                  >
                    Нет разделов с вкладки «Рабочее проектирование». Добавьте
                    разделы там сначала.
                  </div>
                ) : (
                  (() => {
                    const searchLower = (qualitySectionSearch || "")
                      .toLowerCase()
                      .trim();
                    const filtered = searchLower
                      ? workTabSections.filter((s) =>
                          (s.name || "").toLowerCase().includes(searchLower),
                        )
                      : workTabSections;
                    const alreadyInQuality = new Set(
                      displayRoles.map((r) => r.role),
                    );
                    const available = filtered.filter(
                      (s) => !alreadyInQuality.has(s.name),
                    );
                    if (available.length === 0) {
                      return (
                        <div
                          style={{
                            padding: "16px",
                            color: "#666",
                            fontSize: "14px",
                          }}
                        >
                          {filtered.length === 0
                            ? "Ничего не найдено"
                            : "Все эти разделы уже добавлены."}
                        </div>
                      );
                    }
                    return available.map((section) => {
                      const checked = selectedQualitySections.includes(
                        section.id,
                      );
                      return (
                        <label
                          key={section.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "8px 6px",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setSelectedQualitySections((prev) =>
                                e.target.checked
                                  ? [...prev, section.id]
                                  : prev.filter((x) => x !== section.id),
                              );
                            }}
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                            }}
                          />
                          <span className="section-label-text">
                            <span className="section-label-name">{section.name}</span>
                            {section.description && (
                              <span className="section-label-description">
                                {section.description}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    });
                  })()
                )}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button
                onClick={handleCancelAddQualitySection}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: "white",
                  color: "#666",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleConfirmAddQualitySection}
                disabled={selectedQualitySections.length === 0}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "4px",
                  backgroundColor:
                    selectedQualitySections.length > 0 ? "#2A5E38" : "#ccc",
                  color: "white",
                  cursor:
                    selectedQualitySections.length > 0
                      ? "pointer"
                      : "not-allowed",
                  fontSize: "14px",
                  opacity: selectedQualitySections.length > 0 ? 1 : 0.6,
                }}
              >
                Добавить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default QualityTab;
